import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Engineering,
  Speed,
  Warning,
  TrendingUp,
  Assessment,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text, Box as ThreeBox } from '@react-three/drei';
import { User, Asset, Component } from '../types';
import { Socket } from 'socket.io-client';

interface AssetViewerProps {
  user: User;
  socket: Socket | null;
}

interface ComponentSelection {
  componentId: string;
  componentName: string;
  componentType: string;
  healthIndex?: number;
  healthStatus: string;
  cadGeometryId?: string;
  assetName: string;
  sensorCount: number;
  recentSensorData: any[];
  healthHistory: any[];
  selectedAt: string;
}

const AssetViewer: React.FC<AssetViewerProps> = ({ user, socket }) => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [components, setComponents] = useState<Component[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<ComponentSelection | null>(null);
  const [loading, setLoading] = useState(true);
  const [showComponentDetail, setShowComponentDetail] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);

  useEffect(() => {
    if (assetId) {
      loadAssetData();
    }

    if (socket) {
      socket.on('component_selected', handleComponentSelection);
      socket.on('component_sync', handleComponentSync);
    }

    return () => {
      if (socket) {
        socket.off('component_selected', handleComponentSelection);
        socket.off('component_sync', handleComponentSync);
      }
    };
  }, [assetId, socket]);

  const loadAssetData = async () => {
    try {
      setLoading(true);
      
      // Mock data - in production, this would come from API calls
      const mockAsset: Asset = {
        id: assetId!,
        name: 'My Rotator 6',
        type: 'Rotator',
        specifications: {},
        organizationId: user.organizationId,
        organizationName: 'Default Organization',
        status: 'active',
        componentCount: 8,
        avgHealthIndex: 85.2,
        createdAt: '2025-10-01T10:00:00Z',
        updatedAt: '2025-10-04T10:00:00Z',
      };

      const mockComponents: Component[] = [
        {
          id: 'comp-1',
          name: 'Front Bearing',
          type: 'Bearing',
          assetId: assetId!,
          assetName: 'My Rotator 6',
          specifications: {},
          healthIndex: 92.5,
          healthStatus: 'normal',
          sensorCount: 3,
          createdAt: '2025-10-01T10:00:00Z',
          updatedAt: '2025-10-04T10:00:00Z',
        },
        {
          id: 'comp-2',
          name: 'Motor',
          type: 'Motor',
          assetId: assetId!,
          assetName: 'My Rotator 6',
          specifications: {},
          healthIndex: 78.3,
          healthStatus: 'caution',
          sensorCount: 4,
          createdAt: '2025-10-01T10:00:00Z',
          updatedAt: '2025-10-04T10:00:00Z',
        },
        {
          id: 'comp-3',
          name: 'Gear Box',
          type: 'GearBox',
          assetId: assetId!,
          assetName: 'My Rotator 6',
          specifications: {},
          healthIndex: 65.1,
          healthStatus: 'warning',
          sensorCount: 2,
          createdAt: '2025-10-01T10:00:00Z',
          updatedAt: '2025-10-04T10:00:00Z',
        },
        {
          id: 'comp-4',
          name: 'Main Shaft',
          type: 'Shaft',
          assetId: assetId!,
          assetName: 'My Rotator 6',
          specifications: {},
          healthIndex: 45.8,
          healthStatus: 'critical',
          sensorCount: 1,
          createdAt: '2025-10-01T10:00:00Z',
          updatedAt: '2025-10-04T10:00:00Z',
        },
      ];

      setAsset(mockAsset);
      setComponents(mockComponents);

    } catch (error) {
      console.error('Error loading asset data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComponentSelection = (data: ComponentSelection) => {
    setSelectedComponent(data);
    setShowComponentDetail(true);
  };

  const handleComponentSync = (data: any) => {
    console.log('Component sync received:', data);
    // Update component data in real-time
  };

  const handleComponentClick = (componentId: string) => {
    setSelectedComponentId(componentId);
    if (socket) {
      socket.emit('subscribe_component', componentId);
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return 'success';
      case 'caution': return 'warning';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getHealthStatusIcon = (status: string) => {
    switch (status) {
      case 'normal': return <TrendingUp />;
      case 'caution': return <Speed />;
      case 'warning': return <Warning />;
      case 'critical': return <Warning />;
      default: return <Engineering />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading asset data...
        </Typography>
      </Container>
    );
  }

  if (!asset) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Asset not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {asset.name}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {asset.type} • {asset.componentCount} components • Health: {asset.avgHealthIndex?.toFixed(1)}%
      </Typography>

      <Grid container spacing={3}>
        {/* 3D CAD Viewer */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                3D CAD Model
              </Typography>
              <div style={{ height: "500px", border: "1px solid #ddd", borderRadius: "4px" }}>
                <Canvas camera={{ position: [5, 5, 5] }}>
                  <ambientLight intensity={0.5} />
                  <pointLight position={[10, 10, 10]} />
                  
                  {/* Mock 3D components */}
                  {components.map((component, index) => (
                    <ThreeBox
                      key={component.id}
                      position={[index * 2 - 3, 0, 0]}
                      args={[1, 1, 1]}
                      onClick={() => handleComponentClick(component.id)}
                      onPointerOver={(e) => {
                        e.stopPropagation();
                        document.body.style.cursor = 'pointer';
                      }}
                      onPointerOut={(e) => {
                        e.stopPropagation();
                        document.body.style.cursor = 'auto';
                      }}
                    >
                      <meshStandardMaterial 
                        color={
                          component.healthStatus === 'normal' ? '#4caf50' :
                          component.healthStatus === 'caution' ? '#ff9800' :
                          component.healthStatus === 'warning' ? '#ff5722' :
                          '#f44336'
                        }
                      />
                    </ThreeBox>
                  ))}
                  
                  <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
                  
                  {/* Component labels */}
                  {components.map((component, index) => (
                    <Text
                      key={`label-${component.id}`}
                      position={[index * 2 - 3, 1.5, 0]}
                      fontSize={0.3}
                      color="black"
                      anchorX="center"
                      anchorY="middle"
                    >
                      {component.name}
                    </Text>
                  ))}
                </Canvas>
              </div>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Click on components to view details. Use mouse to rotate, zoom, and pan.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Component List */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Components
              </Typography>
              {components.map((component) => (
                <Paper
                  key={component.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    cursor: 'pointer',
                    border: selectedComponentId === component.id ? '2px solid #1976d2' : '1px solid #ddd',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => handleComponentClick(component.id)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <Typography variant="subtitle1">{component.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {component.type} • {component.sensorCount} sensors
                      </Typography>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {getHealthStatusIcon(component.healthStatus)}
                        <Typography variant="h6" color="primary">
                          {component.healthIndex?.toFixed(1)}%
                        </Typography>
                      </div>
                      <Chip
                        label={component.healthStatus}
                        color={getHealthStatusColor(component.healthStatus)}
                        size="small"
                      />
                    </div>
                  </div>
                </Paper>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Component Detail Dialog */}
      <Dialog
        open={showComponentDetail}
        onClose={() => setShowComponentDetail(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedComponent?.componentName}
        </DialogTitle>
        <DialogContent>
          {selectedComponent && (
            <div>
              <Typography variant="h6" gutterBottom>
                Component Details
              </Typography>
              <Typography variant="body1">
                Type: {selectedComponent.componentType}
              </Typography>
              <Typography variant="body1">
                Health Index: {selectedComponent.healthIndex?.toFixed(1)}%
              </Typography>
              <Typography variant="body1">
                Status: {selectedComponent.healthStatus}
              </Typography>
              <Typography variant="body1">
                Sensors: {selectedComponent.sensorCount}
              </Typography>
              
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => {
                  setShowComponentDetail(false);
                  navigate(`/component/${selectedComponent.componentId}`);
                }}
              >
                View Full Details
              </Button>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowComponentDetail(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AssetViewer;





